/* eslint-disable jsx-a11y/control-has-associated-label */
/* istanbul ignore file */
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/core';
import _isEmpty from 'lodash/isEmpty';
import moment from 'moment-timezone';
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { RiArrowDownSLine } from 'react-icons/ri';
import isUndefined from 'lodash/isUndefined';
import {
  Requisition,
  RequisitionStatus,
  SalaryUnits,
  ShiftDetail,
  Shifts,
} from '../../../../../firebase/firestore/documents/requisition';
import { useStoreState } from '../../../../../models/hooks';
import colors from '../../../../../styles/colors';
import { isRequisitionAvailable } from '../../../../../utils/RequisitionEffectiveComparisionWithThresholdValue';
import { drawerCSS } from '../../../../calendar/CalendarView.Style';
import { reactDatePickerCSS } from '../../../../calendar/scheduleSidebar/ReactDatePicker.Style';
import { useMessagingViewContext } from '../../../MessagingViewContext';
import { tableStyle } from '../../header/actionsDropdown/HireWithRequisitionStyle';
import ViewRequisitionDetailUtils from './ViewRequisitionDetailUtils';
import { CandidateRequisitionOverlap } from '../../header/CandidateRequisitionOverlap';
import { AdditionalNoteEditor } from '../../../../../app/components/AdditionalNoteEditor';
import { OfferConditionsType } from '../../../../../firebase/firestore/documents/candidateAudit';
import { weekDays, weekEnds } from '../../../../../utils/uiUtils';
import { LanguageMap } from '../../../../../firebase/firestore/documents/user';
import { getConsistentDateFormat } from '../../../../../utils/dateUtils';

export type ViewRequisitionDetailProps = {
  isOpen: boolean;
  onClose: () => void;
  showReqStatus?: boolean;
  requisition?: Requisition;
  shouldCompare?: boolean;
  requisitionActiveThresholdDaysPosition?: number;
  shiftIntersection?: Shifts;
  availability?: Shifts;
  expectedWorkHour?: { unit: string; value: string };
  offeredSalary?: { salary: string; salaryUnit: string; customSalaryUnit?: string };
  dashboard?: boolean;
  offerTerms?: OfferConditionsType | undefined;
};

export const ViewRequisitionDetail = ({
  isOpen,
  onClose,
  requisition,
  showReqStatus,
  shouldCompare,
  requisitionActiveThresholdDaysPosition,
  shiftIntersection,
  availability,
  expectedWorkHour,
  offeredSalary,
  dashboard,
  offerTerms,
}: ViewRequisitionDetailProps): JSX.Element => {
  const { t, i18n } = useTranslation('requisition');
  const selectedRequisition = requisition;

  const [showComparision, setShowComparision] = useState<boolean>(false);
  const requisitionActiveThresholdDaysAccount = useStoreState(
    (state) => state.app.accounts?.configuration?.requisitionActiveThresholdDays,
  );

  const [offerCondition, setOfferCondition] = useState<OfferConditionsType | undefined>('');

  const isOfferConditionsEnabled = useStoreState((s) => s.app.accounts?.configuration?.offerConditionsEnabled);

  const {
    isOpen: isOfferConditionsModalOpen,
    onOpen: onOfferConditionsModalOpen,
    onClose: onOfferConditionsModalClose,
  } = useDisclosure();

  const getColorScheme = (status: RequisitionStatus) => {
    switch (status) {
      case RequisitionStatus.PROCESSING:
        return 'teal';
      case RequisitionStatus.FILLED:
        return 'green';
      case RequisitionStatus.CANCELLED:
        return 'red';
      default:
        return 'blue';
    }
  };
  const { selectedSeeker } = useMessagingViewContext();
  /* istanbul ignore next */
  const candidateAvailabilities = (availability || selectedSeeker?.newAvailabilities) ?? null;
  const candidateNightAvailabilities = selectedSeeker?.nightAvailabilities ?? null;

  /* istanbul ignore next */
  const isAvailabilitiesMatchedToRequisition = (reqAvailabilities?: ShiftDetail, day?: string) => {
    if (!shouldCompare || !reqAvailabilities) {
      return true;
    }

    if (reqAvailabilities.start_1 < reqAvailabilities.end_1) {
      if (candidateAvailabilities && day && candidateAvailabilities[day] && reqAvailabilities) {
        const startTime1 = new Date(`2000-01-01T${candidateAvailabilities[day].start_1}:00`);
        const endTime1 = new Date(`2000-01-01T${candidateAvailabilities[day].end_1}:00`);
        const startTime2 = new Date(`2000-01-01T${reqAvailabilities.start_1}:00`);
        const endTime2 = new Date(`2000-01-01T${reqAvailabilities.end_1}:00`);
        return startTime1 <= startTime2 && endTime1 >= endTime2;
      }
    } else if (reqAvailabilities.start_1 > reqAvailabilities.end_1) {
      if (candidateNightAvailabilities && day && candidateNightAvailabilities[day] && reqAvailabilities) {
        const startTime1 = new Date(`2000-01-01T${candidateNightAvailabilities[day].start_1}:00`);
        const endTime1 = new Date(`2000-01-01T${candidateNightAvailabilities[day].end_1}:00`);
        const startTime2 = new Date(`2000-01-01T${reqAvailabilities.start_1}:00`);
        const endTime2 = new Date(`2000-01-01T${reqAvailabilities.end_1}:00`);
        return startTime1 <= startTime2 && endTime1 >= endTime2;
      }
    }

    return false;
  };

  const onGetColorByIndex = (index: number) => {
    if (index % 2) return '#edf2f7';
    return '#ffffff';
  };

  // helper function
  const getDayRange = (day: string) => {
    const daysOfWeek = [...weekDays, ...weekEnds];
    const currentIndex = daysOfWeek.indexOf(day);
    const nextDay = currentIndex < daysOfWeek.length - 1 ? daysOfWeek[currentIndex + 1] : daysOfWeek[0];
    return `${t(`common:days.${day}`)} - ${t(`common:days.${nextDay}`)}`;
  };

  const normalizeRequisitionEndTime = (time: string): string => {
    return time === '00:00' ? '24:00' : time;
  };

  const handleOfferConditionsModal = () => {
    onOfferConditionsModalOpen();
  };

  // istanbul ignore next
  const handleAdditionalNote = (offer: LanguageMap<string> | undefined) => {
    setOfferCondition(offer);
  };

  // istanbul ignore next
  React.useEffect(() => {
    if (offerTerms) {
      setOfferCondition(offerTerms);
    }
  }, [offerTerms]);

  return (
    <Box>
      <Modal
        id="ViewRequisitionDetailModal"
        isOpen={isOpen}
        onClose={onClose}
        closeOnOverlayClick
        size="4xl"
        scrollBehavior="inside"
        isCentered={false}
      >
        <ModalOverlay>
          <ModalContent>
            <ModalCloseButton data-testid="requisition-modal-close" />
            <ModalBody css={[drawerCSS, reactDatePickerCSS]}>
              <Box>
                {selectedRequisition ? (
                  <Box>
                    <Flex mt="16px">
                      <Text fontSize={16} fontWeight={700} mr="12px">
                        {t('requisitionDetails')}
                      </Text>
                      {selectedRequisition.seasonal && (
                        <Text fontSize="xs" fontWeight="bold" d="inline-flex" alignItems="center">
                          <Badge
                            variant="subtle"
                            colorScheme="blue"
                            mr="12px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            color={colors.persianBlue}
                            background="#263DBA1A"
                          >
                            {t('seasonal')}
                          </Badge>
                        </Text>
                      )}

                      <Text fontSize="xs" fontWeight="bold" d="inline-flex" alignItems="center">
                        <Badge
                          variant="subtle"
                          colorScheme="blue"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          mr="4px"
                          color={colors.persianBlue}
                          background="#263DBA1A"
                        >
                          {selectedRequisition.fullTime ? t('fullTime') : t('partTime')}
                        </Badge>
                        {`${
                          selectedRequisition.fullTime
                            ? ''
                            : `: ${selectedRequisition.partTimeHours?.toFixed(2)} ${t('partTimeHoursPerWeek')}`
                        } `}
                      </Text>
                      {showReqStatus && selectedRequisition.status && (
                        <Text fontSize="xs" fontWeight="bold" d="inline-flex" alignItems="center">
                          <Badge
                            variant="subtle"
                            colorScheme={getColorScheme(selectedRequisition.status)}
                            ml="6px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            {t(`requisition:${selectedRequisition.status}`)}
                          </Badge>
                        </Text>
                      )}
                    </Flex>
                    <Divider mt="16px" />
                    {!dashboard && <ViewRequisitionDetailUtils requisition={requisition} />}
                    {expectedWorkHour && expectedWorkHour.value && (
                      <Flex mt="12px">
                        <Text fontSize="14px" fontWeight="700" mr={2}>
                          {t('candidateHistory:expectedWorkHours')}
                        </Text>
                        <Text fontSize="14px" fontWeight="400">
                          {`${expectedWorkHour.value} ${t('common:hrsWeek')}`}
                        </Text>
                      </Flex>
                    )}
                    <Flex mt="16px" alignItems="center">
                      <Text fontSize="14px" fontWeight="700">
                        {t('requisitionID')}
                      </Text>
                      <Text pl="6px">{selectedRequisition.requisitionId}</Text>
                    </Flex>
                    <Flex mt="16px" alignItems="center">
                      {selectedRequisition?.effectiveDate && (
                        <Flex w="50%" alignItems="center">
                          <Text fontSize="14px" fontWeight="700">
                            {t('effectiveTime')}
                          </Text>
                          <Text pl="6px">{getConsistentDateFormat(selectedRequisition?.effectiveDate, 'full')}</Text>
                          {
                            /* istanbul ignore next */
                            !isRequisitionAvailable(
                              selectedRequisition?.effectiveDate,
                              requisitionActiveThresholdDaysAccount,
                              requisitionActiveThresholdDaysPosition,
                            ) && (
                              <Flex alignItems="center">
                                <Tooltip
                                  placement="top"
                                  size="xl"
                                  label={t('effectiveTimeInfo', {
                                    requisitionActiveThresholdDays: moment(selectedRequisition.effectiveDate)
                                      .subtract(
                                        requisitionActiveThresholdDaysPosition || requisitionActiveThresholdDaysAccount,
                                        'days',
                                      )
                                      .locale(i18n.language)
                                      .format('ll'),
                                  })}
                                  aria-label="value?.label"
                                  shouldWrapChildren
                                >
                                  <IoMdInformationCircleOutline
                                    color={colors.red[500]}
                                    style={{ cursor: 'pointer', marginLeft: '6px' }}
                                  />
                                </Tooltip>
                              </Flex>
                            )
                          }
                        </Flex>
                      )}
                    </Flex>

                    <Flex mt="16px" alignItems="center">
                      {selectedRequisition?.wage && (
                        <Flex w="50%" alignItems="center">
                          <Text fontSize="14px" fontWeight="700">
                            {t('wages')}
                          </Text>
                          <Text pl="6px">
                            {`${`$${
                              selectedRequisition?.wage?.value
                                ? selectedRequisition?.wage?.value?.toLocaleString(i18n.language, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })
                                : /* istanbul ignore next */ ''
                            }`} ${`${
                              selectedRequisition?.wage.maxValue
                                ? ` - $${selectedRequisition?.wage.maxValue?.toLocaleString(i18n.language, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}`
                                : ''
                            }/`} ${
                              selectedRequisition?.wage.unit === SalaryUnits.CUSTOM
                                ? selectedRequisition?.wage?.customUnit
                                : t(selectedRequisition?.wage.unit.toLowerCase())
                            }`}
                          </Text>
                        </Flex>
                      )}
                      {offeredSalary && (
                        <Flex w="50%" justifyContent="flex-end" alignItems="center">
                          <Text fontSize="14px" fontWeight="700">
                            {t('salaryOffered')}
                          </Text>
                          <Text pl="6px">
                            $
                            {`${`${Number(offeredSalary.salary)?.toLocaleString(i18n.language, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}/`} ${
                              /* istanbul ignore next */
                              offeredSalary.salaryUnit === SalaryUnits.CUSTOM.toLowerCase()
                                ? offeredSalary.customSalaryUnit
                                : t(offeredSalary.salaryUnit.toLowerCase())
                            }`}
                          </Text>
                        </Flex>
                      )}
                    </Flex>
                    {selectedRequisition.seasonal && selectedRequisition?.endDate && (
                      <Flex mt="12px">
                        <Text fontSize="14px" fontWeight="700">
                          {t('seasonalEndDate')}
                        </Text>
                        <Text pl="6px">{getConsistentDateFormat(selectedRequisition?.endDate, 'full')}</Text>
                      </Flex>
                    )}

                    {shiftIntersection && (
                      <Box mt="10px">
                        <Box>
                          <CandidateRequisitionOverlap
                            intersectionShift={shiftIntersection}
                            selectedRequisition={selectedRequisition}
                            candidateAvailabilities={candidateAvailabilities}
                            candidateNightAvailabilities={candidateNightAvailabilities}
                          />
                        </Box>
                      </Box>
                    )}
                    {shouldCompare ? (
                      <Box mt="16px">
                        <Flex
                          alignItems="center"
                          justifyContent="space-between"
                          cursor="pointer"
                          data-testid="showReqAndCandidateAvabilityBtn"
                          onClick={() => setShowComparision(!showComparision)}
                          _hover={{ background: colors.gray[100] }}
                          p="6px 0px"
                        >
                          <Text fontSize="14px" fontWeight="700" mb="6px">
                            {t('compare')}
                          </Text>
                          <Box>
                            <RiArrowDownSLine />
                          </Box>
                        </Flex>

                        {showComparision && (
                          <Box mt="16px" css={tableStyle}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr>
                                  <th style={{ width: '20%', textAlign: 'left', padding: '8px', fontWeight: 700 }} />
                                  <th style={{ width: '40%', textAlign: 'left', padding: '8px', fontWeight: 700 }}>
                                    {t('candidateAvailability')}
                                  </th>
                                  <th style={{ width: '40%', textAlign: 'left', padding: '8px', fontWeight: 700 }}>
                                    {t('requisitionShifts')}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {weekDays.concat(weekEnds).map((day, index) => {
                                  const reqShift = selectedRequisition?.shifts?.[day];
                                  const candidateDay = candidateAvailabilities?.[day] || {};
                                  const candidateNight = candidateNightAvailabilities?.[day] || {};

                                  const newIsMatched = (
                                    reqStartTime: string,
                                    reqEndTime: string,
                                    intersectionShift: ShiftDetail | undefined,
                                    reqShiftIndex: number,
                                  ) => {
                                    if (isUndefined(intersectionShift)) return false;
                                    if (reqShiftIndex === 1) {
                                      return (
                                        (reqStartTime === intersectionShift?.start_1 &&
                                          normalizeRequisitionEndTime(reqEndTime) === intersectionShift.end_1) ||
                                        (reqStartTime === intersectionShift?.start_3 &&
                                          normalizeRequisitionEndTime(reqEndTime) === intersectionShift.end_3)
                                      );
                                    }

                                    if (reqShiftIndex === 2) {
                                      return (
                                        (reqStartTime === intersectionShift?.start_2 &&
                                          normalizeRequisitionEndTime(reqEndTime) === intersectionShift.end_2) ||
                                        (reqStartTime === intersectionShift?.start_4 &&
                                          normalizeRequisitionEndTime(reqEndTime) === intersectionShift.end_4)
                                      );
                                    }

                                    return false;
                                  };

                                  return (
                                    <Fragment key={day}>
                                      {day === 'sat' && (
                                        <tr
                                          style={{
                                            height: '16px',
                                            borderLeft: '1px solid white',
                                            borderRight: '1px solid white',
                                          }}
                                        />
                                      )}
                                      <tr
                                        key={day}
                                        style={{ backgroundColor: index % 2 === 0 ? `${colors.input.background}` : '#ffffff' }}
                                      >
                                        {/* Day Column */}
                                        <td align="center">
                                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
                                            <div style={{ padding: '0px', fontWeight: 600 }}>{t(`common:days.${day}`)}</div>
                                          </div>
                                        </td>

                                        {/* Candidate Availability Column */}
                                        <td style={{ padding: '8px' }}>
                                          <Flex direction="column">
                                            {[1].map((shiftIndex) => {
                                              const startKey = `start_${shiftIndex}` as keyof typeof candidateDay;
                                              const endKey = `end_${shiftIndex}` as keyof typeof candidateDay;

                                              return (
                                                <Fragment key={shiftIndex}>
                                                  {candidateDay[startKey] && candidateDay[endKey] ? (
                                                    <Text fontSize={15} fontWeight="normal" mb="2px">
                                                      {`${moment(candidateDay[startKey], ['HH:mm']).format(
                                                        'LT',
                                                      )} - ${moment(candidateDay[endKey], ['HH:mm']).format('LT')}`}
                                                    </Text>
                                                  ) : (
                                                    <Text>--</Text>
                                                  )}
                                                  {candidateNight[startKey] && candidateNight[endKey] ? (
                                                    <Text fontSize={15} fontWeight="normal" mb="2px">
                                                      {`${moment(candidateNight[startKey], ['HH:mm']).format(
                                                        'LT',
                                                      )} - ${moment(candidateNight[endKey], ['HH:mm']).format('LT')}`}
                                                    </Text>
                                                  ) : (
                                                    <Text>--</Text>
                                                  )}
                                                </Fragment>
                                              );
                                            })}
                                          </Flex>
                                        </td>

                                        {/* Desired Availability (Requisition Shifts) Column */}
                                        <td style={{ padding: '8px' }}>
                                          <Flex direction="column">
                                            {[1, 2].map((shiftIndex) => {
                                              const startKey = `start_${shiftIndex}` as keyof typeof reqShift;
                                              const endKey = `end_${shiftIndex}` as keyof typeof reqShift;

                                              const reqStart = reqShift?.[startKey];
                                              const reqEnd = reqShift?.[endKey];

                                              return reqStart && reqEnd ? (
                                                <Text
                                                  key={shiftIndex}
                                                  fontSize={15}
                                                  fontWeight="normal"
                                                  mb="2px"
                                                  style={{
                                                    backgroundColor: newIsMatched(
                                                      reqStart,
                                                      reqEnd,
                                                      shiftIntersection?.[day],
                                                      shiftIndex,
                                                    )
                                                      ? colors.aeroBlue
                                                      : '#ffe9e9',
                                                    display: 'inline-block',
                                                    padding: '2px 4px',
                                                    borderRadius: '4px',
                                                  }}
                                                >
                                                  {`${moment(reqStart, ['HH:mm']).format('LT')} - ${moment(reqEnd, [
                                                    'HH:mm',
                                                  ]).format('LT')}`}
                                                </Text>
                                              ) : (
                                                <Text>--</Text>
                                              );
                                            })}
                                          </Flex>
                                        </td>
                                      </tr>
                                    </Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Box mt="16px">
                        <Text fontSize="14px" fontWeight="700" mb="12px">
                          {t('shifts')}
                        </Text>
                        <Flex mt="16px" alignItems="baseline" css={tableStyle} style={{ display: 'flex' }}>
                          <Box w="50%">
                            <table>
                              {weekDays.map((day, index) => {
                                const reqShift = selectedRequisition?.shifts && selectedRequisition?.shifts[day];
                                const isAnotherRequisitionOnSameDayAvailable =
                                  reqShift && reqShift.start_2 && reqShift.end_2 && reqShift.start_2 < reqShift.end_2;

                                const isAnotherRequisitionOnDifferentDayAvailable =
                                  reqShift && reqShift.start_2 && reqShift.end_2 && reqShift.start_2 >= reqShift.end_2;

                                const isBothRequisitionOnDifferentDay =
                                  reqShift &&
                                  reqShift.start_2 &&
                                  reqShift.end_2 &&
                                  reqShift.start_2 >= reqShift.end_2 &&
                                  reqShift.start_1 >= reqShift.end_1;

                                const isFirstRequisitionOnDifferentDayAndSecondOnSame =
                                  reqShift &&
                                  reqShift.start_2 &&
                                  reqShift.end_2 &&
                                  reqShift.start_2 < reqShift.end_2 &&
                                  reqShift.start_1 >= reqShift.end_1;
                                return (
                                  <Flex direction="column" key={day}>
                                    {isFirstRequisitionOnDifferentDayAndSecondOnSame && (
                                      <tr
                                        style={{
                                          background: isAvailabilitiesMatchedToRequisition(
                                            selectedRequisition?.shifts?.[day],
                                            day,
                                          )
                                            ? /* istanbul ignore next */ onGetColorByIndex(index)
                                            : colors.red[100],
                                          borderTop: '1px solid #edf2f7',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                        }}
                                      >
                                        <td>{t(`common:days.${day}`)}</td>

                                        <td style={{ float: 'right' }}>
                                          <Flex>
                                            <Text fontSize={16}>
                                              {`${moment(reqShift?.start_2, ['HH:mm']).format('LT')} - ${moment(reqShift?.end_2, [
                                                'HH:mm',
                                              ]).format('LT')}`}
                                            </Text>
                                          </Flex>
                                        </td>
                                      </tr>
                                    )}

                                    <tr
                                      style={{
                                        background: isAvailabilitiesMatchedToRequisition(selectedRequisition?.shifts?.[day], day)
                                          ? /* istanbul ignore next */ onGetColorByIndex(index)
                                          : colors.red[100],
                                        borderTop: '1px solid #edf2f7',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                      }}
                                    >
                                      <td>
                                        {reqShift && (reqShift.start_1 > reqShift.end_1 || reqShift.isOvernightShift)
                                          ? getDayRange(day)
                                          : t(`common:days.${day}`)}
                                      </td>

                                      <td style={{ display: 'flex', padding: 0, alignItems: 'center' }}>
                                        <td style={{ float: 'right', padding: 0 }}>
                                          {(isAnotherRequisitionOnSameDayAvailable || isBothRequisitionOnDifferentDay) &&
                                          !isFirstRequisitionOnDifferentDayAndSecondOnSame ? (
                                            <Badge
                                              background={
                                                isAvailabilitiesMatchedToRequisition(selectedRequisition?.shifts?.[day], day)
                                                  ? '#e9f0ff'
                                                  : '#ffe9e9'
                                              }
                                            >
                                              <Flex>
                                                {reqShift ? (
                                                  <Text fontSize={15} fontWeight="normal">
                                                    {`${moment(reqShift.start_1, ['HH:mm']).format(
                                                      'LT',
                                                    )} - ${moment(reqShift.end_1, ['HH:mm']).format('LT')}`}
                                                  </Text>
                                                ) : (
                                                  <Text>---</Text>
                                                )}
                                              </Flex>
                                            </Badge>
                                          ) : (
                                            <Flex paddingRight={2}>
                                              {reqShift ? (
                                                <Text>
                                                  {`${moment(reqShift.start_1, ['HH:mm']).format('LT')} - ${moment(
                                                    reqShift.end_1,
                                                    ['HH:mm'],
                                                  ).format('LT')}`}
                                                </Text>
                                              ) : (
                                                <Text>---</Text>
                                              )}
                                            </Flex>
                                          )}
                                        </td>

                                        {(isAnotherRequisitionOnSameDayAvailable || isBothRequisitionOnDifferentDay) &&
                                          !isFirstRequisitionOnDifferentDayAndSecondOnSame && (
                                            <td style={{ float: 'right' }}>
                                              <Badge
                                                background={
                                                  isAvailabilitiesMatchedToRequisition(selectedRequisition?.shifts?.[day], day)
                                                    ? '#e9f0ff'
                                                    : '#ffe9e9'
                                                }
                                              >
                                                <Flex>
                                                  <Text fontSize={15} fontWeight="normal">
                                                    {`${moment(reqShift?.start_2, ['HH:mm']).format(
                                                      'LT',
                                                    )} - ${moment(reqShift?.end_2, ['HH:mm']).format('LT')}`}
                                                  </Text>
                                                </Flex>
                                              </Badge>
                                            </td>
                                          )}
                                      </td>
                                    </tr>

                                    {isAnotherRequisitionOnDifferentDayAvailable && !isBothRequisitionOnDifferentDay && (
                                      <tr
                                        style={{
                                          background: isAvailabilitiesMatchedToRequisition(
                                            selectedRequisition?.shifts?.[day],
                                            day,
                                          )
                                            ? /* istanbul ignore next */ onGetColorByIndex(index)
                                            : colors.red[100],
                                          borderTop: '1px solid #edf2f7',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                        }}
                                      >
                                        <td>{getDayRange(day)}</td>

                                        <td style={{ float: 'right' }}>
                                          <Flex>
                                            <Text fontSize={16}>
                                              {`${moment(reqShift?.start_2, ['HH:mm']).format('LT')} - ${moment(reqShift?.end_2, [
                                                'HH:mm',
                                              ]).format('LT')}`}
                                            </Text>
                                          </Flex>
                                        </td>
                                      </tr>
                                    )}
                                  </Flex>
                                );
                              })}
                            </table>
                          </Box>

                          <Box
                            w="50%"
                            ml={
                              /* istanbul ignore next */
                              selectedRequisition?.shifts && !_isEmpty(selectedRequisition?.shifts) ? '12px' : '0px'
                            }
                          >
                            <table>
                              {weekEnds.map((day, index) => {
                                const reqShift = selectedRequisition?.shifts && selectedRequisition?.shifts[day];

                                const isAnotherRequisitionOnSameDayAvailable =
                                  reqShift && reqShift.start_2 && reqShift.end_2 && reqShift.start_2 < reqShift.end_2;

                                const isAnotherRequisitionOnDifferentDayAvailable =
                                  reqShift && reqShift.start_2 && reqShift.end_2 && reqShift.start_2 >= reqShift.end_2;

                                const isBothRequisitionOnDifferentDay =
                                  reqShift &&
                                  reqShift.start_2 &&
                                  reqShift.end_2 &&
                                  reqShift.start_2 > reqShift.end_2 &&
                                  reqShift.start_1 > reqShift.end_1;

                                const isFirstRequisitionOnDifferentDayAndSecondOnSame =
                                  reqShift &&
                                  reqShift.start_2 &&
                                  reqShift.end_2 &&
                                  reqShift.start_2 < reqShift.end_2 &&
                                  reqShift.start_1 >= reqShift.end_1;
                                return (
                                  <Flex direction="column" key={day}>
                                    {isFirstRequisitionOnDifferentDayAndSecondOnSame && (
                                      <tr
                                        style={{
                                          background: isAvailabilitiesMatchedToRequisition(
                                            selectedRequisition?.shifts?.[day],
                                            day,
                                          )
                                            ? /* istanbul ignore next */ onGetColorByIndex(index)
                                            : colors.red[100],
                                          borderTop: '1px solid #edf2f7',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                        }}
                                      >
                                        <td>{t(`common:days.${day}`)}</td>

                                        <td style={{ float: 'right' }}>
                                          <Flex>
                                            <Text fontSize={16}>
                                              {`${moment(reqShift?.start_2, ['HH:mm']).format('LT')} - ${moment(reqShift?.end_2, [
                                                'HH:mm',
                                              ]).format('LT')}`}
                                            </Text>
                                          </Flex>
                                        </td>
                                      </tr>
                                    )}

                                    <tr
                                      style={{
                                        background: isAvailabilitiesMatchedToRequisition(selectedRequisition?.shifts?.[day], day)
                                          ? /* istanbul ignore next */ onGetColorByIndex(index)
                                          : colors.red[100],
                                        borderTop: '1px solid #edf2f7',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                      }}
                                    >
                                      <td>
                                        {reqShift && (reqShift.start_1 > reqShift.end_1 || reqShift.isOvernightShift)
                                          ? getDayRange(day)
                                          : t(`common:days.${day}`)}
                                      </td>

                                      <td style={{ display: 'flex', padding: 0, alignItems: 'center' }}>
                                        <td style={{ float: 'right', padding: 0 }}>
                                          {(isAnotherRequisitionOnSameDayAvailable || isBothRequisitionOnDifferentDay) &&
                                          !isFirstRequisitionOnDifferentDayAndSecondOnSame ? (
                                            <Badge
                                              background={
                                                isAvailabilitiesMatchedToRequisition(selectedRequisition?.shifts?.[day], day)
                                                  ? '#e9f0ff'
                                                  : '#ffe9e9'
                                              }
                                            >
                                              <Flex>
                                                {reqShift ? (
                                                  <Text fontSize={15} fontWeight="normal">
                                                    {`${moment(reqShift.start_1, ['HH:mm']).format(
                                                      'LT',
                                                    )} - ${moment(reqShift.end_1, ['HH:mm']).format('LT')}`}
                                                  </Text>
                                                ) : (
                                                  <Text>---</Text>
                                                )}
                                              </Flex>
                                            </Badge>
                                          ) : (
                                            <Flex paddingRight={2}>
                                              {reqShift ? (
                                                <Text>
                                                  {`${moment(reqShift.start_1, ['HH:mm']).format('LT')} - ${moment(
                                                    reqShift.end_1,
                                                    ['HH:mm'],
                                                  ).format('LT')}`}
                                                </Text>
                                              ) : (
                                                <Text>---</Text>
                                              )}
                                            </Flex>
                                          )}
                                        </td>

                                        {(isAnotherRequisitionOnSameDayAvailable || isBothRequisitionOnDifferentDay) &&
                                          !isFirstRequisitionOnDifferentDayAndSecondOnSame && (
                                            <td style={{ float: 'right' }}>
                                              <Badge
                                                background={
                                                  isAvailabilitiesMatchedToRequisition(selectedRequisition?.shifts?.[day], day)
                                                    ? '#e9f0ff'
                                                    : '#ffe9e9'
                                                }
                                              >
                                                <Flex>
                                                  <Text fontSize={15} fontWeight="normal">
                                                    {`${moment(reqShift?.start_2, ['HH:mm']).format(
                                                      'LT',
                                                    )} - ${moment(reqShift?.end_2, ['HH:mm']).format('LT')}`}
                                                  </Text>
                                                </Flex>
                                              </Badge>
                                            </td>
                                          )}
                                      </td>
                                    </tr>

                                    {isAnotherRequisitionOnDifferentDayAvailable && !isBothRequisitionOnDifferentDay && (
                                      <tr
                                        style={{
                                          background: isAvailabilitiesMatchedToRequisition(
                                            selectedRequisition?.shifts?.[day],
                                            day,
                                          )
                                            ? /* istanbul ignore next */ onGetColorByIndex(index)
                                            : colors.red[100],
                                          borderTop: '1px solid #edf2f7',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                        }}
                                      >
                                        <td>{getDayRange(day)}</td>

                                        <td style={{ float: 'right' }}>
                                          <Flex>
                                            <Text fontSize={16}>
                                              {`${moment(reqShift?.start_2, ['HH:mm']).format('LT')} - ${moment(reqShift?.end_2, [
                                                'HH:mm',
                                              ]).format('LT')}`}
                                            </Text>
                                          </Flex>
                                        </td>
                                      </tr>
                                    )}
                                  </Flex>
                                );
                              })}
                            </table>
                          </Box>
                        </Flex>
                      </Box>
                    )}
                    <Box css={tableStyle} w="60%">
                      {
                        /* istanbul ignore next */ (selectedRequisition?.minHours ||
                          selectedRequisition?.maxHours ||
                          selectedRequisition?.guaranteedHours ||
                          selectedRequisition?.guaranteedHours === 0 ||
                          selectedRequisition?.minHours === 0 ||
                          selectedRequisition?.maxHours === 0) && (
                          <Text fontSize="14px" fontWeight="700" mt="12px" mb="6px">
                            {t('contractedHours')}
                          </Text>
                        )
                      }
                      <table style={{ width: '50%' }}>
                        {(selectedRequisition?.minHours || selectedRequisition?.minHours === 0) && (
                          <tr>
                            <td>{t('minimum')}</td>
                            <td style={{ textAlign: 'end' }}>
                              {selectedRequisition?.minHours.toLocaleString(i18n.language, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                          </tr>
                        )}
                        {(selectedRequisition?.maxHours || selectedRequisition?.maxHours === 0) && (
                          <tr>
                            <td>{t('maximum')}</td>
                            <td style={{ textAlign: 'end' }}>
                              {selectedRequisition?.maxHours.toLocaleString(i18n.language, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                          </tr>
                        )}
                        {(selectedRequisition?.guaranteedHours || selectedRequisition?.guaranteedHours === 0) && (
                          <tr>
                            <td>{t('guaranteedHours')}</td>
                            <td style={{ textAlign: 'end' }}>
                              {selectedRequisition?.guaranteedHours.toLocaleString(i18n.language, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                          </tr>
                        )}
                      </table>
                    </Box>

                    {offerTerms && isOfferConditionsEnabled && (
                      <Box>
                        <Button colorScheme="blue" mt={4} fontSize="15px" onClick={() => handleOfferConditionsModal()}>
                          {t('additionalOffer')}
                        </Button>
                      </Box>
                    )}

                    <Box mt="14px">
                      <Text fontSize="14px" fontWeight="700" mb="6px">
                        {t('recruiterNote')}
                      </Text>
                      <Text fontSize="14px">{selectedRequisition?.note}</Text>
                    </Box>
                  </Box>
                ) : (
                  <Box m={10} fontSize="16px" fontWeight="700">
                    {t('couldNotLoadRequisition')}
                  </Box>
                )}
              </Box>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      </Modal>

      {isOfferConditionsModalOpen && (
        // istanbul ignore next
        <Modal isOpen={isOfferConditionsModalOpen} onClose={onOfferConditionsModalClose} isCentered size="3xl">
          <ModalOverlay />
          <ModalContent px={3} py={2}>
            <ModalCloseButton />
            <ModalBody mt={2}>
              <AdditionalNoteEditor
                additionalNote={offerCondition}
                handleNote={handleAdditionalNote}
                isReadOnly
                showToolbar={false}
                showHeader
                height="50vh"
              />
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
};
